import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import Summary from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import Image from '../../widgets/Image';

import * as css from './UXDesign.m.css';
import FAQ from '../../widgets/FAQ';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const consultingImg = require('../../assets/images/illustration/consulting.svg');
const designImg = require('../../assets/images/illustration/design.svg');
const designToolsImg = require('../../assets/images/logo-illustration/design-tools.svg');
const platformsImg = require('../../assets/images/illustration/app-dev.svg');
const rapidImg = require('../../assets/images/icon-illustration/rapid-prototyping-design.svg');
const uiDesignImg = require('../../assets/images/icon-illustration/ui-design.svg');
const userResearchImg = require('../../assets/images/icon-illustration/user-research.svg');

const factory = create();

export const UXDesign = factory(function UXDesign() {
	return (
		<div classes={css.root}>
			<Head
				title="Enterprise Software UX Design | SitePen"
				description="SitePen’s enterprise UX design team excels at designing great user experiences as part of our software development process to create the best possible products for our customers."
			/>

			<Hero background="level3Red">
				{{
					xlTitle: (
						<span>
							<abbr classes={css.titleAbbr} title="User Experience">
								UX
							</abbr>{' '}
							Design
						</span>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse paddingBottom="md">
						{{
							title: (
								<virtual>
									Make Products So Good
									<br />
									Your Customers Won’t Leave
								</virtual>
							),
							blurb: (
								<virtual>
									Few things affect a customer as much as their first impression
									of a product. Having a strong, thoughtful user experience (UX)
									in your enterprise application will not only improve your
									business objectives, but it will also give your users a clean
									experience that they will enjoy and remember. Your customers
									will come to associate a delightful experience with your
									products and develop loyalty to your brand. A happy customer
									doesn’t shop around among your competitors. They don’t file
									support tickets. They simply use your product because it enables
									them to do their work. A better UX will always lead to more
									engagement from your customers and a larger customer lifetime
									value for your business.
								</virtual>
							),
							image: <Image src={platformsImg} />
						}}
					</Summary>
					<Summary>
						{{
							title: 'Enterprise Software UX Design',
							blurb: (
								<virtual>
									<p>
										In the enterprise software industry, UX design is a critical
										aspect of any successful product. Enterprise applications
										frequently deal in greater complexity and scale than
										consumer software. Because of this, it can be even harder to
										build software that both builds empathy with customers and
										solves business needs. These challenges necessitate the
										importance of a strong UX vision that balances the user and
										their needs with business goals and requirements.
									</p>
									<p>
										Whether a product is used to edit ten records or ten
										million, whether it’s a single application or a platform for
										an entire suite - a poor design will confuse users, lead to
										errors, and risk stunting the product’s growth in the
										future. Beginning with a strong UX foundation will ensure
										that the design will scale to accommodate future features
										and that even the most complex interactions will feel second
										nature to the customer.
									</p>
								</virtual>
							),
							image: <Image src={designImg} size="md" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							blurb: (
								<virtual>
									<p>
										At SitePen, we believe that quality is the single most
										important aspect of any project we undertake. Our commitment
										to delivering clean, elegant user experiences is a critical
										aspect to our approach to software design.
									</p>
									<p>
										You will start to experience this as soon as you reach out
										to us. Our Sales and Project Management teams work with you
										to understand your goals and expectations, identifying the
										characteristics that have the most significant influence on
										the application's design.
									</p>
									<p>
										Our design team will then develop the solution with these
										goals in mind. Through the use of wireframes, rapid
										prototyping, and visual UI design, they will create a
										solution that will enhance your user’s experience and will
										also scale easily into the future. Whether working directly
										with your team or SitePen’s own engineers, they will prepare
										production-ready assets and work with developers to see the
										work through to implementation.
									</p>
								</virtual>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'UX Design Services',
							blurb: (
								<virtual>
									User experience design embodies several disciplines that all
									work together towards a single finished product. To your users
									and customers, it’s just the product they see. But to our
									designers, it’s a combination of visual design, storyboarding,
									research, and user testing that leads to that seamless user
									experience.
								</virtual>
							),
							items: [
								{
									title: 'Wireframes and Prototypes',
									blurb: (
										<virtual>
											<p>
												One of the greatest benefits to prioritizing the
												design process of your application is the ability to
												quickly explore solutions without the overhead of
												development costs and timelines. By utilizing
												wireframes, prototypes, and other low-fidelity
												methods, our designers will quickly iterate through
												different ideas and solutions. Rather than trying to
												imagine the product, you’ll be able to click through
												actual prototype screens and get an idea of the
												final user experience.
											</p>
											<p>
												By taking this approach, it’s easy to find the
												perfect solution that feels right for your customers
												as well as for the needs of your business. This
												enables you to move forward into development with
												the confidence that you’re building the right
												product and that you’ll have happy customers who
												love using it.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={rapidImg}
											lazy
											size="icon"
											align="center"
											constrainedBy="height"
										/>
									)
								},
								{
									title: 'User Research',
									blurb: (
										<virtual>
											<p>
												Prototyping goes hand in hand with user research.
												You may have a strong vision for your product’s
												design, but there is nothing better than getting
												feedback first hand from your customers. This can be
												incorporated into any stage of the design and
												development process, but is a huge benefit in the
												early stages. User research can validate your ideas
												as well as expose unforeseen challenges to
												delivering a holistic user experience. Our UX
												designers are experts at presenting their designs to
												users and letting them get direct experience with
												the solution.
											</p>
											<p>
												Our team will engage users to get vital feedback
												about their expectations and needs. This is a great
												way to surface user requirements that aren’t
												necessarily found in the core objectives but will
												make a lasting impact on the UX of the product. By
												incorporating the user into the design process, you
												ensure that your product will deliver real value to
												them.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={userResearchImg}
											lazy
											size="icon"
											align="center"
											constrainedBy="height"
										/>
									)
								},
								{
									title: 'UI Design',
									blurb: (
										<virtual>
											<p>
												User interface design (sometimes referred to as user
												interaction design) applies to a product’s visual
												look and feel. Once an application has a solidified
												user journey and flow, UI design is used to create a
												clear visual language for the user to understand.
												They will be able to clearly distinguish navigation
												elements, forms, inputs, and other controls in the
												design, and understand how to interact with those
												elements.
											</p>
											<p>
												This step is a crucial part of a well designed
												product, because it delivers consistency to the user
												and gives them visual cues to help them distinguish
												between interactive elements and ordinary content.
											</p>
											<p>
												SitePen’s designers are experts at hand crafting
												interfaces that not only match the visual style of
												your brand and business, but that are also clear and
												intuitive to your customers. We focus on consistency
												to make implementation easy for developers and the
												entire experience familiar to the end user.
											</p>
										</virtual>
									),
									image: (
										<Image
											src={uiDesignImg}
											lazy
											size="icon"
											align="center"
											constrainedBy="height"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'Technologies We Use',
							blurb: (
								<p>
									SitePen utilizes the best leading-edge technology for our design
									process. We have in-depth experience with industry standard
									applications such as Sketch, Figma, and the Adobe Suite. Our
									abilities are not limited to mockups and static screenshots, but
									also incorporate clickable prototypes built with InVision,
									Framer, Axure, and Figma. We leverage existing design libraries
									and design systems such as Google’s Material Design and
									Bootstrap, and we are experts at developing custom systems
									ourselves. Whatever the need, we have experience with the right
									tools and technology to deliver fast and beautiful results for
									your project.
								</p>
							),
							image: <Image src={designToolsImg} lazy />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={consultingImg} lazy />,
							title: 'Choosing the Right UX Design Company',
							blurb: (
								<virtual>
									<p>
										Choosing your design partner can be a critical part of your
										project’s success. Too many teams lack the experience to
										know what to look for in a UX design company. It’s important
										that you find someone who not only has a great portfolio,
										but also demonstrates that they can solve hard visual
										problems. A strong design team will challenge your ideas and
										show you new ways to approach your product.
									</p>
									<p>
										The designers at SitePen have comprehensive experience
										solving tough challenges. We’ve worked closely with
										enterprise companies to build tools for their internal teams
										as well as their external customers that are engaging,
										intuitive, and easy to use. We bring experience, speed, and
										agility to the design process.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Enterprise UX Design FAQs">
					{[
						{
							title: 'How Does UX Impact Software?',
							content: (
								<p>
									UX can shape the way your users interact with your product. If
									things aren’t intuitive and easy, users will be reluctant to
									engage and may even abandon your application entirely. Building
									easy to use software is a core requirement of keeping your users
									happy and ensuring the success of your product.
								</p>
							)
						},
						{
							title: 'Should I Care About My UX Design?',
							content: (
								<p>
									Yes. UX design is something that is easy to disregard as a “nice
									to have, but not necessary” aspect of your business. But to your
									customers, the UX is something that will encapsulate the entire
									product. If you cut corners or don’t make UX design a priority,
									you will likely be left with a diminishing user base and plenty
									of unhappy customers. Building with the user in mind will ensure
									that your application stays relevant and continues to provide
									real value to your customers.
								</p>
							)
						},
						{
							title: 'What is the Difference Between UI and UX?',
							content: (
								<p>
									User experience design (UX design) is a broad term used to
									describe how the user engages and interacts with a piece of
									software. It incorporates many things, including user research,
									prototyping, user stories, and the general flow of the
									application. User interface design (UI design) is the portion of
									design that is focused on the visual look and feel of the
									application. Many times these terms are used interchangeably,
									but UI design is only a piece of the broader UX design process.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default UXDesign;
